import React, { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VirtualizedAutocomplete from '../../../../../../components/Dropdown/VirtualizedAutocomplete';
import { ContactTitles as buildingContactRoles } from '../../config/formDataConfig'

const columnLabels = {
  contactName: 'Contact Name',
  mainPhone: 'Main Phone',
  cellPhone: 'Cell Phone',
  emailAddress: 'Email Address',
};

const BuildingContactTable = ({
  assignedContacts,
  setAssignedContacts,
  contactOptions,
  newContactId,
  setNewContactId,
  addContact,
  removeContact,
  handleRoleChange,
  isEditMode,
  toggleNewContactForm,
}) => {
  const [visibleColumns, setVisibleColumns] = useState({
    contactName: true,
    mainPhone: false, // Hidden by default in edit mode
    cellPhone: true,
    emailAddress: false, // Hidden by default in edit mode
  });

  const viewModeColumns = {
    contactName: true,
    mainPhone: true,
    cellPhone: true,
    emailAddress: true,
  };

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [errors, setErrors] = useState({});

  // Function to handle changes in the contact selection
  const handleContactSelectionChange = (event, newValue) => {
    setNewContactId(newValue ? newValue.key : '');
  };

  // Function to handle toggling column visibility
  const handleColumnToggle = (column) => {
    setVisibleColumns((prevColumns) => ({
      ...prevColumns,
      [column]: !prevColumns[column],
    }));
  };

  // Function to handle entering edit mode for a contact
  const handleEditContact = (index) => {
    setAssignedContacts((prevContacts) =>
      prevContacts.map((contact, idx) =>
        idx === index ? { ...contact, isEditing: true, originalData: { ...contact } } : contact
      )
    );
  };

  // Function to handle canceling edits
  const handleCancelEdit = (index) => {
    setAssignedContacts((prevContacts) =>
      prevContacts.map((contact, idx) =>
        idx === index
          ? { ...contact.originalData, isEditing: false, originalData: undefined }
          : contact
      )
    );

    // Clear errors for this contact
    const contactId = assignedContacts[index].contactId;
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors[contactId];
      return updatedErrors;
    });
  };

  // Function to validate contact fields
  const validateContactFields = (contact) => {
    const requiredFields = {
      contactName: 'Contact Name',
      mainPhone: 'Main Phone',
      emailAddress: 'Email Address',
    };

    const newErrors = {};

    Object.keys(requiredFields).forEach((field) => {
      if (!contact[field]?.toString().trim()) {
        newErrors[field] = `${requiredFields[field]} is required.`;
      }
    });

    return newErrors;
  };

  const handleSaveEdit = (index) => {
    const contact = assignedContacts[index];
  
    // Optional: Add validation logic if needed
    const validationErrors = validateContactFields(contact);
    if (Object.keys(validationErrors).length > 0) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [contact.contactId]: validationErrors,
      }));
      return; // Stop here if validation fails
    }
  
    // Clear errors
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      delete updatedErrors[contact.contactId];
      return updatedErrors;
    });
  
    // Update contact state
    setAssignedContacts((prevContacts) =>
      prevContacts.map((c, idx) =>
        idx === index ? { ...c, isEditing: false, originalData: undefined } : c
      )
    );
  };
  
  

  // Function to handle input changes for contact fields
  const handleContactFieldChange = (e, index) => {
    const { name, value } = e.target;
    setAssignedContacts((prevContacts) =>
      prevContacts.map((contact, idx) =>
        idx === index ? { ...contact, [name]: value } : contact
      )
    );

    // Clear the error for this field
    const contactId = assignedContacts[index].contactId;
    setErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (updatedErrors[contactId]) {
        delete updatedErrors[contactId][name];
        // If no more errors for this contact, remove the contact from errors
        if (Object.keys(updatedErrors[contactId]).length === 0) {
          delete updatedErrors[contactId];
        }
      }
      return updatedErrors;
    });
  };

  // Function to update contact on the server
//   const updateContact = async (contactID, updatedContact) => {
//     try {
//         const response = await fetch(`${API_BASE_URL}/api/setup/contacts/${contactID}`, {
//             method: 'PUT',
//             headers: {
//                 'Content-Type': 'application/json',
//             },
//             body: JSON.stringify(updatedContact),
//             credentials: 'include', // Equivalent to withCredentials: true
//         });

//         if (!response.ok) {
//             throw new Error(`API responded with status ${response.status}`);
//         }

//         const responseData = await response.json();
//         return responseData;
//     } catch (error) {
//         console.error('Error updating contact:', error.message || error);
//         throw error;
//     }
// };


  // Column toggle dropdown (only in edit mode)
  const columnToggleDropdown = isEditMode && (
    <div className="relative">
      <button
        onClick={() => setIsDropdownOpen((prev) => !prev)}
        className="px-4 py-2 border rounded-md bg-gray-200 hover:bg-gray-300 font-semibold"
      >
        Toggle Columns
      </button>
      {isDropdownOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-10">
          <div className="p-2">
            {Object.keys(visibleColumns).map((column) => (
              <label key={column} className="flex items-center space-x-2 mt-2">
                <input
                  type="checkbox"
                  checked={visibleColumns[column]}
                  onChange={() => handleColumnToggle(column)}
                />
                <span>{columnLabels[column] || column}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  if (!isEditMode) {
    // View Mode Table (unchanged)
    const filledContactData = buildingContactRoles.map((role, index) => {
      const contactForRole = assignedContacts.find((contact) => contact[role.key]);
      return contactForRole
        ? { ...contactForRole, contactTitle: role.label }
        : {
            contactId: `blank-${index}`,
            contactTitle: role.label,
            contactName: 'N/A',
            mainPhone: 'N/A',
            cellPhone: 'N/A',
            emailAddress: 'N/A',
          };
    });

    return (
      <div className="mb-4">
        <table className="min-w-full bg-white rounded-lg shadow-lg overflow-hidden border-2 border-gray-200">
          <thead className="bg-gray-200">
            <tr>
              <th className="px-4 py-2 text-left font-bold text-sss-blue-sel">Contact Title</th>
              {Object.keys(viewModeColumns).map(
                (column) =>
                  viewModeColumns[column] && (
                    <th key={column} className="px-4 py-2 text-left font-bold text-sss-blue-sel">
                      {columnLabels[column] || column}
                    </th>
                  )
              )}
            </tr>
          </thead>
          <tbody>
            {filledContactData.map((contact) => (
              <tr key={contact.contactId}>
                <td className="px-4 py-2 border text-sss-blue font-semibold">
                  {contact.contactTitle}
                </td>
                {Object.keys(viewModeColumns).map(
                  (column) =>
                    viewModeColumns[column] && (
                      <td key={column} className="px-4 py-2 border">
                        {contact[column] || 'N/A'}
                      </td>
                    )
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Edit Mode Table (with fixed width and overflow)
  return (
    <div className="mb-4">
      {/* Editable Table */}
      <div className="flex mb-4 items-center">
        <div className="w-1/3 mr-6">
          <VirtualizedAutocomplete
            options={contactOptions.map((contact) => ({
              key: contact?.contactId?.toString(),
              value: contact?.contactName || 'Unnamed Contact',
            }))}
            value={contactOptions.find((contact) => contact.contactId === newContactId) || null}
            onChange={handleContactSelectionChange}
            label="Select Contact"
            optionKey="key"
            optionLabel="value"
            fullWidth
          />
        </div>
        <button
          className="px-4 py-2 border rounded-md bg-sss-blue text-white font-semibold mr-2"
          onClick={addContact}
        >
          Add Contact
        </button>
        <button
          className="px-4 py-2 mr-4 border rounded-md bg-sss-accent-green text-white font-semibold"
          onClick={toggleNewContactForm}
        >
          New Contact
        </button>
        {columnToggleDropdown}
      </div>
      {/* Editable Table with fixed width and overflow */}
      <div className="overflow-x-auto">
        <table
          className="min-w-full bg-white rounded-lg shadow-lg overflow-hidden border-2 border-gray-200"
          style={{ minWidth: '800px' }} // Adjust the minWidth as needed
        >
          <thead className="bg-gray-200">
            <tr>
              <th className="px-4 py-2"></th>
              {Object.keys(visibleColumns).map(
                (column) =>
                  visibleColumns[column] && (
                    <th key={column} className="px-4 py-2 text-left font-bold text-sss-blue-sel">
                      {columnLabels[column] || column}
                    </th>
                  )
              )}
              {buildingContactRoles.map((role) => (
                <th key={role.key} className="px-4 py-2 text-left font-bold text-sss-blue-sel">
                  {role.label}
                </th>
              ))}
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {assignedContacts.map((contact, index) => (
              <tr key={contact.contactId}>
                <td className="p-2 border text-center">
                {contact.isEditing ? (
  <>
    <button
      onClick={() => handleSaveEdit(index)}
      className="text-green-500 hover:bg-gray-300 rounded-full"
    >
      <CheckIcon />
    </button>
    <button
      onClick={() => handleCancelEdit(index)}
      className="text-red-500 hover:bg-gray-300 rounded-full"
    >
      <CloseIcon />
    </button>
  </>
) : (
  <button
    onClick={() => handleEditContact(index)}
    className="border-gray-300 hover:bg-gray-200 rounded-xl transition ease-in-out border-2 p-2"
  >
    <EditIcon />
  </button>
)}

                </td>
                {contact.isEditing
                  ? Object.keys(visibleColumns).map(
                      (column) =>
                        visibleColumns[column] && (
                          <td key={column} className="px-2 py-2 border">
                            <input
                              type="text"
                              name={column}
                              value={contact[column]}
                              onChange={(e) => handleContactFieldChange(e, index)}
                              className={`w-full border rounded-md p-1 ${
                                errors[contact.contactId]?.[column] ? 'border-red-500' : ''
                              }`}
                            />
                            {errors[contact.contactId]?.[column] && (
                              <p className="text-red-500 text-sm">
                                {errors[contact.contactId][column]}
                              </p>
                            )}
                          </td>
                        )
                    )
                  : Object.keys(visibleColumns).map(
                      (column) =>
                        visibleColumns[column] && (
                          <td key={column} className="px-2 py-2 border">
                            {contact[column] || 'N/A'}
                          </td>
                        )
                    )}
                {buildingContactRoles.map((role) => (
                  <td key={role.key} className="border text-center">
                    <input
                      type="checkbox"
                      checked={contact[role.key] || false}
                      onChange={(e) => handleRoleChange(e, index, role.key)}
                      className="transform scale-125"
                    />
                  </td>
                ))}
                <td className="p-2 border text-center">
                  <button
                    onClick={() => removeContact(index)}
                    className="bg-red-400 hover:bg-red-500 text-white rounded-xl transition ease-in-out border-2 p-2"
                  >
                    <DeleteIcon />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BuildingContactTable;